<template>
  <div v-if="isOpen" class="file-viewer-modal-overlay" @click.self="closeModal" @contextmenu.prevent>
    <div class="file-viewer-modal" @contextmenu.prevent>
      <!-- Header -->
      <div class="file-viewer-header">
        <div class="d-flex gap-1 align-items-center" style="min-width: 0; flex: 1;">
          <i :class="getFileIcon()" class="font-size-18"></i>
          
          <div class="d-flex flex-column" style="min-width: 0;">
            <div class="file-viewer-title">
              <span>{{ fileName }}</span>
            </div>
            <span v-if="fileDescription" class="file-viewer-description">{{ fileDescription }}</span>
            <span v-if="page" class="file-viewer-page-indicator">Page {{ page }}</span>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button 
            class="file-viewer-details-btn" 
            @click="isShowDetails = !isShowDetails"
            :title="isShowDetails ? 'Hide Details' : 'Show Details'"
          >
            <i class="mdi mdi-information-outline"></i>
          </button>
          <button class="file-viewer-close-btn" @click="closeModal" aria-label="Close">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="file-viewer-body">
        <div class="file-viewer-content">
          <!-- Loading State -->
          <div v-if="loading" class="file-viewer-loading">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3">Loading file...</p>
            <div v-if="downloadProgress > 0" class="progress-container">
              <div class="progress">
                <div 
                  class="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  :style="{ width: downloadProgress + '%' }"
                  :aria-valuenow="downloadProgress" 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {{ downloadProgress }}%
                </div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="file-viewer-error">
            <i class="mdi mdi-alert-circle-outline text-danger"></i>
            <p class="mt-3 text-danger">{{ error }}</p>
            <button class="btn btn-primary mt-3" @click="loadFile">
              <i class="mdi mdi-refresh text-white"></i>
            </button>
          </div>

          <!-- PDF Viewer -->
          <div v-else-if="isPDF && fileUrl" class="file-viewer-pdf">
            <vue-pdf-app
              :config="pdfConfig"
              :pdf="fileUrl"
              @pages-rendered="onPagesRendered"
              @open="onPdfOpen"
              style="height: 100%; width: 100%;"
            />
          </div>

          <!-- Image Viewer -->
          <div v-else-if="isImage && fileUrl" class="file-viewer-image">
            <CustomViewer
              :loading="false"
              :isShowDetails="isShowDetails"
              :images="[{ source: fileUrl, fileName: fileName }]"
              style="height: 100%; width: 100%;"
            />
          </div>

          <!-- Unsupported File Type -->
          <div v-else-if="!loading && !isPDF && !isImage" class="file-viewer-unsupported">
            <i class="mdi mdi-file-alert-outline"></i>
            <p class="mt-3">Preview not available for this file type</p>
            <button class="btn btn-primary mt-3" @click="downloadFile">
              <i class="mdi mdi-download"></i> Download File
            </button>
          </div>
        </div>

        <!-- Details Side Panel -->
        <div v-if="isShowDetails" class="file-viewer-details-panel">
          <h6 class="details-panel-title">Document Details</h6>

          <div class="details-panel-content">
            <div class="detail-item">
              <span class="detail-label">Name</span>
              <span class="detail-value">{{ fileName }}</span>
            </div>
            <div v-if="fileDescription" class="detail-item">
              <span class="detail-label">Description</span>
              <span class="detail-value">{{ fileDescription }}</span>
            </div>
            <div v-if="page" class="detail-item">
              <span class="detail-label">Page</span>
              <span class="detail-value">{{ page }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">File Type</span>
              <span class="detail-value text-uppercase">{{ fileType || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Folder Path</span>
              <span 
                class="detail-value detail-link" 
                @click="navigateToFolder"
                title="Go to folder"
              >
                <i class="mdi mdi-folder-outline me-1"></i>{{ folderPath }}
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Location Path</span>
              <span 
                class="detail-value detail-link" 
                @click="navigateToLocation"
                title="Go to location"
              >
                <i class="mdi mdi-map-marker-outline me-1"></i>{{ locationPath }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "vue-pdf-app/dist/icons/main.css";
import { mapActions } from 'vuex';
import Loader from "@/components/layout/loader";
import CustomViewer from "@/components/layout/custom-viewer";

export default {
  name: 'SingleFileViewerModal',
  
  components: {
    "vue-pdf-app": () => ({
      component: import(/* webpackChunkName: "pdf-viewer" */ "vue-pdf-app"),
      loading: Loader
    }),
    CustomViewer
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    citation: {
      type: Object,
      default: null
    }
  },
  
  data() {
    return {
      loading: false,
      error: null,
      fileUrl: null,
      fileBlob: null,
      downloadProgress: 0,
      isShowDetails: false,
      pdfConfig: {
        toolbar: {
          toolbarViewerRight: {
            presentationMode: false,
            openFile: false,
            print: false,
            download: false,
            viewBookmark: false,
          },
        },
      },
    };
  },
  
  computed: {
    fileName() {
      return this.citation?.FileManagerName || 'Unknown File';
    },

    fileDescription() {
      return this.citation?.FileManagerDescription || '';
    },
    
    documentId() {
      return this.citation?.DocumentId;
    },

    locationPath(){
      return this.citation?.Location || '';
    },

    locationId() {
      return this.citation?.LocationId || 0;
    },

    folderPath() {
      return this.citation?.FolderLocation || '';
    },

    folderId() {
      return this.citation?.FolderId || 0;
    },
    
    page() {
      return this.citation?.Page;
    },
    
    fileType() {
      const fileName = this.citation?.FileName || '';
      const extension = fileName.includes('.') 
        ? fileName.split('.').pop().toLowerCase()
        : '';
      return extension;
    },
    
    isPDF() {
      return this.fileType === 'pdf';
    },
    
    isImage() {
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(this.fileType);
    },
  },
  
  watch: {
    isOpen(newValue) {
      if (newValue && this.documentId) {
        this.loadFile();
      } else if (!newValue) {
        this.cleanup();
      }
    }
  },
  
  methods: {
    ...mapActions({
      readFileContent: 'aiChat/fileView/readFileContent'
    }),
    
    async loadFile() {
      if (!this.documentId) {
        this.error = 'File ID is missing';
        return;
      }
      
      this.loading = true;
      this.error = null;
      this.downloadProgress = 0;
      
      try {
        const blob = await this.readFileContent({
          id: this.documentId,
          onProgress: (progressData) => {
            this.downloadProgress = progressData.progress;
          }
        });
        
        this.fileBlob = blob;
        
        // Create object URL for the blob
        if (this.isPDF) {
          this.fileUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        } else {
          this.fileUrl = URL.createObjectURL(blob);
        }
        
        this.loading = false;
        this.downloadProgress = 0;
      } catch (err) {
        console.error('Error loading file:', err);
        this.error = err.response?.data?.message || 'Failed to load file. Please try again.';
        this.loading = false;
        this.downloadProgress = 0;
      }
    },

    onPagesRendered(pdfApp) {
      if (!pdfApp) return;

      // Set scale to auto first
      setTimeout(() => {
        pdfApp.pdfViewer.currentScaleValue = "auto";

        // Navigate to the citation page if specified
        if (this.page && pdfApp.pdfViewer) {
          const pageNum = parseInt(this.page, 10);
          if (pageNum > 0 && pageNum <= pdfApp.pdfViewer.pagesCount) {
            pdfApp.pdfViewer.currentPageNumber = pageNum;
          }
        }
      });
    },

    onPdfOpen() {
      // Additional setup after PDF is opened if needed
    },

    navigateToFolder() {
      if (this.folderId) {
        this.closeModal();
        this.$changeFolder(this, this.folderId);
      }
    },

    navigateToLocation() {
      if (this.locationId) {
        this.closeModal();
        this.$changeLocation(this, this.locationId);
      }
    },
    
    downloadFile() {
      if (!this.fileUrl || !this.fileBlob) return;
      
      // Create download link
      const link = document.createElement('a');
      link.href = this.fileUrl;
      link.download = this.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    closeModal() {
      this.$emit('close');
    },
    
    cleanup() {
      // Revoke object URL to free memory
      if (this.fileUrl) {
        URL.revokeObjectURL(this.fileUrl);
        this.fileUrl = null;
      }
      this.fileBlob = null;
      this.error = null;
      this.downloadProgress = 0;
      this.isShowDetails = false;
    },
    
    getFileIcon() {
      const iconMap = {
        'pdf': 'mdi mdi-file-pdf-box text-danger',
        'doc': 'mdi mdi-file-word-box text-primary',
        'docx': 'mdi mdi-file-word-box text-primary',
        'xls': 'mdi mdi-file-excel-box text-success',
        'xlsx': 'mdi mdi-file-excel-box text-success',
        'jpg': 'mdi mdi-file-image text-info',
        'jpeg': 'mdi mdi-file-image text-info',
        'png': 'mdi mdi-file-image text-info',
        'gif': 'mdi mdi-file-image text-info',
        'default': 'mdi mdi-file-document'
      };
      
      return iconMap[this.fileType] || iconMap['default'];
    }
  },
  
  beforeDestroy() {
    this.cleanup();
  }
};
</script>

<style scoped>
.file-viewer-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10600;
  padding: 20px;
}

.file-viewer-modal {
  background: #3d3d3d;
  border-radius: 8px;
  width: 100%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.file-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: #292929;
  border-radius: 8px 8px 0 0;
}

.file-viewer-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;
  flex: 1;
  min-width: 0;
}

.file-viewer-title span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-viewer-description {
  color: #a6b0cf;
  font-weight: 500;
  font-size: 0.68rem;
}

.file-viewer-page-indicator {
  color: #556ee6;
  font-weight: 500;
  font-size: 0.7rem;
}

.file-viewer-details-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 18px;
  cursor: pointer;
  color: #a6b0cf;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.file-viewer-details-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.3);
}

.file-viewer-close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #a6b0cf;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.file-viewer-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.file-viewer-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  border-radius: 0 0 8px 8px;
}

.file-viewer-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3d3d3d;
  position: relative;
}

.file-viewer-loading,
.file-viewer-error,
.file-viewer-unsupported {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #a6b0cf;
}

.file-viewer-loading .spinner-border {
  width: 3rem;
  height: 3rem;
}

.progress-container {
  width: 100%;
  max-width: 400px;
  margin-top: 20px;
}

.progress {
  height: 24px;
  border-radius: 12px;
  background-color: #32394e;
  overflow: hidden;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background-color: #556ee6;
  transition: width 0.3s ease;
}

.file-viewer-error i {
  font-size: 24px;
  color: #f46a6a;
}

.file-viewer-pdf {
  width: 100%;
  height: 100%;
}

.file-viewer-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

/* Details Side Panel */
.file-viewer-details-panel {
  width: 280px;
  min-width: 280px;
  background: #2a3042;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  overflow-y: auto;
  padding: 16px;
}

.details-panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.details-panel-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #a6b0cf;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 13px;
  color: #e9ecef;
  word-break: break-word;
}

.detail-link {
  cursor: pointer;
  color: #556ee6;
  transition: color 0.2s;
  display: flex;
  align-items: flex-start;
}

.detail-link:hover {
  color: #7b93f0;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .file-viewer-details-panel {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 10;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
  }

  .file-viewer-body {
    position: relative;
  }
}
</style>
